import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { citationIdFromHref, frameIdFromSrc, isAllowedImageSrc, safeUrl, withCitationLinks } from '../citations.ts';
import SopImageMarkupOverlay from '../markup/SopImageMarkupOverlay.tsx';
import type { SopMarkupAnnotation } from '../markup/types.ts';

interface Props {
  body: string;
  frames: Record<string, string>;
  /** Markers that exist for this SOP; unknown markers render as plain text instead of dead chips. */
  knownMarkers: Set<string>;
  onCite: (marker: string) => void;
  /** Optional image markup (isolated from text highlight/comment). */
  sectionId?: string;
  sectionHeading?: string;
  markupAnnotations?: SopMarkupAnnotation[];
  markupMode?: boolean;
  markupEditable?: boolean;
  onMarkupAdd?: (item: SopMarkupAnnotation) => void;
  onMarkupRemove?: (id: string) => void;
}

const SopSectionBody: React.FC<Props> = ({
  body,
  frames,
  knownMarkers,
  onCite,
  sectionId,
  sectionHeading,
  markupAnnotations = [],
  markupMode = false,
  markupEditable = false,
  onMarkupAdd,
  onMarkupRemove,
}) => (
  <div className="sop-body max-w-none space-y-3 text-sm leading-6 text-gray-800 [&_ol]:list-decimal [&_ol]:pl-6 [&_ul]:list-disc [&_ul]:pl-6 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-gray-200 [&_td]:px-3 [&_td]:py-1.5 [&_th]:border [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_th]:px-3 [&_th]:py-1.5 [&_th]:text-left [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1">
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      urlTransform={safeUrl}
      components={{
        // Images with markup toolbars cannot live inside <p> (invalid HTML / broken clicks).
        p: ({ children }) => {
          const arr = React.Children.toArray(children);
          const hasImageMarkup = arr.some(
            (child) =>
              React.isValidElement(child) &&
              (child.type === SopImageMarkupOverlay ||
                (typeof child.props === 'object' &&
                  child.props != null &&
                  'frameId' in (child.props as Record<string, unknown>))),
          );
          if (hasImageMarkup) {
            return <div className="my-2">{children}</div>;
          }
          return <p>{children}</p>;
        },
        a: ({ href, children }) => {
          const marker = citationIdFromHref(href);
          if (marker) {
            if (!knownMarkers.has(marker)) return <span>{children}</span>;
            return (
              <button
                type="button"
                onClick={() => onCite(marker)}
                className="mx-0.5 inline-flex items-center rounded bg-blue-50 px-1.5 text-xs font-medium text-blue-700 align-baseline hover:bg-blue-100"
                aria-label={`Show source for citation ${marker.slice(1)}`}
              >
                {marker.slice(1)}
              </button>
            );
          }
          return (
            <a href={href} target="_blank" rel="noreferrer" className="text-blue-700 underline">
              {children}
            </a>
          );
        },
        img: ({ src, alt }) => {
          if (!isAllowedImageSrc(src)) {
            return <span className="text-xs italic text-gray-400">[image removed]</span>;
          }
          const id = frameIdFromSrc(src);
          const url = id ? frames[id] : undefined;
          if (!url) return <span className="text-xs italic text-gray-400">[screenshot unavailable]</span>;

          if (
            id &&
            sectionId &&
            onMarkupAdd &&
            onMarkupRemove
          ) {
            return (
              <SopImageMarkupOverlay
                frameId={id}
                sectionId={sectionId}
                sectionHeading={sectionHeading || ''}
                src={url}
                alt={alt ?? ''}
                annotations={markupAnnotations}
                markupMode={markupMode}
                canEdit={markupEditable}
                onAdd={onMarkupAdd}
                onRemove={onMarkupRemove}
              />
            );
          }

          // span (not figure/div): react-markdown often wraps images in <p>, and <figure> inside <p> is invalid.
          return (
            <span className="my-2 block">
              <img src={url} alt={alt ?? ''} loading="lazy" className="max-h-96 rounded border border-gray-200" />
              {alt ? <span className="mt-1 block text-xs text-gray-500">{alt}</span> : null}
            </span>
          );
        },
      }}
    >
      {withCitationLinks(body)}
    </ReactMarkdown>
  </div>
);

export default SopSectionBody;
